import { z } from "zod";
import { contentType, slugSchema, timestamp } from "./common.js";

/**
 * Discovery surfaces: cross-type related entries, the public "what's new"
 * change feed, and public usage counts.
 */

/** A lightweight, hydrated reference to any content entry. */
export const entrySummary = z.object({
  entryType: contentType,
  entryId: z.string().min(1),
  slug: slugSchema.nullable(),
  title: z.string(),
  description: z.string(),
  published: z.boolean(),
});
export type EntrySummary = z.infer<typeof entrySummary>;

/** One row in the "what's new" feed. */
export const changeItem = entrySummary.extend({
  createdAt: timestamp,
  updatedAt: timestamp,
  /** "new" when created == updated, otherwise "updated". */
  change: z.enum(["new", "updated"]),
});
export type ChangeItem = z.infer<typeof changeItem>;

/** Server-side paginated "what's new" feed page. */
export const whatsNewResult = z.object({
  items: z.array(changeItem),
  total: z.number().int().nonnegative(),
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1),
  hasMore: z.boolean(),
});
export type WhatsNewResult = z.infer<typeof whatsNewResult>;

/**
 * Public per-entry usage counts, keyed by entry id:
 * prompts → times copied, vault → times opened.
 */
export const publicCounts = z.object({
  prompt: z.record(z.string(), z.number().int().nonnegative()),
  vault: z.record(z.string(), z.number().int().nonnegative()),
});
export type PublicCounts = z.infer<typeof publicCounts>;
